import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LifeBuoy, Search, MailOpen, Mail } from "lucide-react";
import api from "@/lib/api";
import { LoadingState, ErrorState, EmptyState } from "@/components/shared/States";
import { PageHeader, FilterBar, SummaryTile } from "@/components/shared/PageChrome";
import { ViewTabs, SearchInput, useListParams } from "@/components/shared/ListChrome";
import { Pagination } from "@/components/shared/StackedList";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 20;
const VIEWS = [{ value: "ALL", label: "All conversations" }, { value: "UNREAD", label: "Unread" }, { value: "AWAITING_REPLY", label: "Awaiting our reply" }];
const initials = (n) => n.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");

export default function SupportInbox() {
  const { view, q, page, setView, setQ, setPage } = useListParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setData(null);
    api.get("/directory/clients/support-inbox", { params: { q: q || undefined, page, page_size: PAGE_SIZE } }).then((r) => setData(r.data)).catch(() => setError(true));
  }, [q, page]);

  if (error) return <ErrorState testId="support-inbox-error" />;

  // Views are derived on the current page's real fields (unread_count / last author) - no extra state is invented.
  const items = data ? data.items.filter((i) => view === "ALL" || (view === "UNREAD" ? i.unread_count > 0 : !i.latest_message.is_staff_reply)) : [];
  const totalUnread = data ? data.items.reduce((sum, i) => sum + i.unread_count, 0) : 0;
  const awaiting = data ? data.items.filter((i) => !i.latest_message.is_staff_reply).length : 0;

  return (
    <div data-testid="support-inbox-page" className="space-y-5">
      <PageHeader title="Support Inbox" subtitle="Every client conversation in one place. Replies are sent from the client's record and appear in their Support thread." />

      {data && (
        <div data-testid="support-inbox-summary" className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <SummaryTile testId="inbox-conversations" label="Conversations" value={data.total} sub="Client organisations with messages" />
          <SummaryTile testId="inbox-unread" label="Unread by you" value={totalUnread} sub="On this page" tone={totalUnread ? "warning" : "default"} />
          <SummaryTile testId="inbox-awaiting" label="Awaiting our reply" value={awaiting} sub="Client sent the last message" tone={awaiting ? "danger" : "default"} />
          <SummaryTile testId="inbox-page" label="Page" value={`${data.page} / ${Math.max(1, Math.ceil(data.total / PAGE_SIZE))}`} />
        </div>
      )}

      <ViewTabs testId="support-inbox-views" tabs={VIEWS} value={view} onChange={setView} counts={data ? { ALL: data.items.length, UNREAD: data.items.filter((i) => i.unread_count > 0).length, AWAITING_REPLY: awaiting } : undefined} />
      <FilterBar testId="support-inbox-filter-bar" summary={data ? `${data.total} conversation${data.total === 1 ? "" : "s"} match` : null}>
        <SearchInput testId="support-inbox-search-input" value={q} onChange={setQ} placeholder="Search clients…" />
      </FilterBar>

      {!data ? (
        <LoadingState testId="support-inbox-loading" />
      ) : items.length === 0 ? (
        <EmptyState testId="support-inbox-empty" icon={LifeBuoy} title={view === "ALL" && !q ? "No conversations yet" : "Nothing in this view"} description={view === "ALL" && !q ? "Client support messages will appear here as soon as one comes in." : "Try another view or clear the search."} />
      ) : (
        <>
          <div className="divide-y divide-jigsaw-border rounded-[10px] border border-jigsaw-border bg-white">
            {items.map((item) => (
              <button key={item.organisation_id} data-testid={`support-inbox-row-${item.organisation_id}`} onClick={() => navigate(`/control-centre/clients/${item.organisation_id}`)}
                className={cn("flex w-full items-center gap-3 px-4 py-3.5 text-left hover:bg-jigsaw-bg", item.unread_count > 0 && "bg-jigsaw-orange/[0.04]")}>
                <span aria-hidden className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-jigsaw-navy/10 text-[12px] font-semibold text-jigsaw-navy">{initials(item.organisation_name)}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className={cn("truncate text-jigsaw-navy", item.unread_count > 0 ? "font-semibold" : "font-medium")}>{item.organisation_name}</p>
                    {item.unread_count > 0 && (
                      <span data-testid={`support-inbox-unread-${item.organisation_id}`} className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-jigsaw-danger px-1.5 text-[11px] font-semibold text-white">{item.unread_count}</span>
                    )}
                    {!item.latest_message.is_staff_reply && <span className="inline-flex items-center gap-1 rounded-full border border-jigsaw-border px-2 py-0.5 text-[11px] text-jigsaw-muted"><Mail className="h-3 w-3" /> Awaiting reply</span>}
                    {item.latest_message.is_staff_reply && <span className="inline-flex items-center gap-1 rounded-full border border-jigsaw-border px-2 py-0.5 text-[11px] text-jigsaw-muted"><MailOpen className="h-3 w-3" /> Replied</span>}
                  </div>
                  <p className="truncate text-sm text-jigsaw-muted">{item.latest_message.is_staff_reply ? `${item.latest_message.author_name || "Jigsaw"}: ` : ""}{item.latest_message.body}</p>
                </div>
                <p className="shrink-0 text-xs text-jigsaw-muted">{new Date(item.latest_message.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
              </button>
            ))}
          </div>
          <Pagination testId="support-inbox-pagination" page={data.page} pageSize={PAGE_SIZE} total={data.total} onPage={setPage} />
        </>
      )}
      {data && view !== "ALL" && data.total > data.items.length && (
        <p data-testid="support-inbox-view-note" className="flex items-center gap-1.5 text-[12px] text-jigsaw-muted"><Search className="h-3 w-3" /> This view filters the current page only ({data.items.length} of {data.total} conversations loaded).</p>
      )}
    </div>
  );
}
